import '../scss/components/_ownerreports.scss'
import React, { useState, useEffect, useMemo } from 'react'
import { get_occupancy_status } from '../apis/services/pgApi'

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

// ─── Shared helpers ──────────────────────────────────────────────────────────

function getHealthSummary(avg) {
  if (avg >= 95) return { text: 'Outstanding occupancy', tag: 'excellent' }
  if (avg >= 90) return { text: 'Excellent occupancy',   tag: 'excellent' }
  if (avg >= 85) return { text: 'Healthy occupancy',     tag: 'healthy'   }
  if (avg >= 80) return { text: 'Needs improvement',     tag: 'needs-improvement' }
  return           { text: 'Attention required',         tag: 'attention' }
}

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rpt-stat-tile" style={{ borderTopColor: accent }}>
      <span className="rpt-stat-value" style={{ color: accent }}>{value}</span>
      <span className="rpt-stat-label">{label}</span>
      {sub && <span className="rpt-stat-sub">{sub}</span>}
    </div>
  )
}

// ─── Occupancy Month Accordion ──────────────────────────────────────────────

function OccupancyMonthAccordion({ occupancyData, selectedYear }) {
  const [expandedYears, setExpandedYears] = useState(() => ({
    [selectedYear]: true
  }))

  const [expandedMonths, setExpandedMonths] = useState(() => ({
    [`${selectedYear}-Jan`]: true
  }))

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }))
  }

  const toggleMonth = (key) => {
    setExpandedMonths(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  // Get available years from data
  const availableYears = Object.keys(occupancyData)
    .map(Number)
    .sort((a, b) => b - a) // Descending (latest first)

  // Room type mapping
  const roomTypeMap = {
    'single': 'Single',
    'double': 'Double',
    'triple': 'Triple',
    'quad': 'Quad',
    'dorm': 'Dorm'
  }

  return (
    <div className="rpt-occupancy-accordion">
      <h3 className="rpt-accordion-title">Month Wise Occupancy</h3>
      
      {availableYears.map(year => {
        const yearData = occupancyData[year] || {}
        const isYearExpanded = expandedYears[year] || false
        const yearMonths = Object.keys(yearData)
        const totalOccupants = Object.values(yearData).reduce(
          (sum, monthData) => sum + (monthData?.length || 0), 0
        )

        return (
          <div key={year} className="rpt-year-accordion">
            <button 
              className="rpt-year-toggle"
              onClick={() => toggleYear(year)}
            >
              <span className="rpt-accordion-chevron">
                {isYearExpanded ? '▼' : '▶'}
              </span>
              <span className="rpt-year-label">{year}</span>
              <span className="rpt-year-count">({totalOccupants} occupants)</span>
            </button>

            {isYearExpanded && (
              <div className="rpt-year-content">
                {yearMonths.map(month => {
                  const monthData = yearData[month] || []
                  const monthKey = `${year}-${month}`
                  const isMonthExpanded = expandedMonths[monthKey] || false

                  return (
                    <div key={month} className="rpt-month-accordion">
                      <button 
                        className="rpt-month-toggle"
                        onClick={() => toggleMonth(monthKey)}
                      >
                        <span className="rpt-accordion-chevron">
                          {isMonthExpanded ? '▼' : '▶'}
                        </span>
                        <span className="rpt-month-label">{month}</span>
                        <span className="rpt-month-count">({monthData.length} occupants)</span>
                      </button>

                      {isMonthExpanded && (
                        <div className="rpt-month-content">
                          {monthData.length > 0 ? (
                            <table className="rpt-occupancy-table">
                              <thead>
                                <tr>
                                  <th>S.No</th>
                                  <th>Tenant Name</th>
                                  <th>Room Type</th>
                                  <th>Room Number</th>
                                  <th>Bed Number</th>
                                  <th>Joining Date</th>
                                  <th>Exit Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {monthData.map((occupant, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{occupant.tenantName}</td>
                                    <td>{roomTypeMap[occupant.roomType] || occupant.roomType}</td>
                                    <td>{occupant.roomNumber}</td>
                                    <td>{occupant.bedNumber}</td>
                                    <td>{occupant.joiningDate}</td>
                                    <td>{occupant.exitDate || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="rpt-empty-message">No occupants this month</p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

const OwnerOccupancyReport = ({ selectedPGId, selectedPG, dateFrom, dateTo, selectedYear, mockMonthlyData }) => {
  const [bedData, setBedData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [target,  setTarget]  = useState(85)

  const { year: currentYear, month: currentMonth } = useMemo(() => {
    const d = new Date(dateTo)
    return { year: d.getFullYear(), month: d.getMonth() + 1 }
  }, [dateTo])

  useEffect(() => {
    if (!selectedPGId) return
    setLoading(true)
    setError(null)
    get_occupancy_status(selectedPGId)
      .then(response => {
        let beds = []
        if (Array.isArray(response)) {
          beds = response.length >= 1 ? response[0] : response
        } else if (response?.data) {
          beds = response.data
        } else {
          beds = response || []
        }
        setBedData(beds)
      })
      .catch(err => setError(err.message || 'Failed to load'))
      .finally(() => setLoading(false))
  }, [selectedPGId])

  const bedStats = useMemo(() => {
    const total    = bedData.length
    const occupied = bedData.filter(b => (b.OccupancyStatus || '').toUpperCase() === 'OCCUPIED').length
    const notice   = bedData.filter(b => (b.OccupancyStatus || '').toUpperCase() === 'NOTICE').length
    const vacant   = total - occupied - notice
    const rate     = total ? ((occupied / total) * 100) : 0
    return { total, occupied, notice, vacant, rate: Math.round(rate * 10) / 10 }
  }, [bedData])

  const monthlyData  = useMemo(() => mockMonthlyData, [mockMonthlyData])
  const rawYearData  = monthlyData[selectedYear] || Array(12).fill(null)
  const maxMonth     = (selectedYear === currentYear) ? currentMonth : 12
  const yearData     = rawYearData.map((val, idx) => (idx + 1 > maxMonth ? null : val))

  const validData  = yearData.filter(v => v !== null && v !== undefined)
  const currentAvg = validData.length > 0 ? validData.reduce((a, b) => a + b, 0) / validData.length : 0
  const prevYearData = monthlyData[selectedYear - 1] || Array(12).fill(null)
  const prevValid    = prevYearData.filter(v => v !== null && v !== undefined)
  const prevAvg      = prevValid.length > 0 ? prevValid.reduce((a, b) => a + b, 0) / prevValid.length : 0
  const vsPrev       = currentAvg - prevAvg

  const peakVal    = validData.length > 0 ? Math.max(...validData) : 0
  const peakIdx    = yearData.indexOf(peakVal)
  const peakMonth  = peakIdx >= 0 ? MONTHS[peakIdx] : '—'
  const lowestVal  = validData.length > 0 ? Math.min(...validData) : 0
  const lowestIdx  = yearData.indexOf(lowestVal)
  const lowestMonth = lowestIdx >= 0 ? MONTHS[lowestIdx] : '—'

  const health    = getHealthSummary(currentAvg)
  const healthTag = health.tag

  const getBarClass = (val) => {
    if (val === null || val === undefined) return 'rpt-bar-future'
    return val >= target ? 'rpt-bar-met' : 'rpt-bar-missed'
  }

  // ─── Mock occupancy data for accordion ────────────────────────────────────

  const occupancyData = useMemo(() => {
    const mockData = {
      2026: {
        'January': [
          { tenantName: 'Rahul Sharma', roomType: 'single', roomNumber: '101', bedNumber: '01', joiningDate: '02 Jan 2026', exitDate: null },
          { tenantName: 'Sneha Patel', roomType: 'double', roomNumber: '102', bedNumber: '01', joiningDate: '05 Jan 2026', exitDate: '18 Jan 2026' },
          { tenantName: 'Arjun Rao', roomType: 'dorm', roomNumber: '201', bedNumber: '01', joiningDate: '08 Jan 2026', exitDate: null },
          { tenantName: 'Priya Nair', roomType: 'single', roomNumber: '103', bedNumber: '01', joiningDate: '12 Jan 2026', exitDate: null },
          { tenantName: 'Vikram Singh', roomType: 'triple', roomNumber: '202', bedNumber: '01', joiningDate: '15 Jan 2026', exitDate: '28 Jan 2026' },
        ],
        'February': [
          { tenantName: 'Meena Dasari', roomType: 'double', roomNumber: '104', bedNumber: '02', joiningDate: '01 Feb 2026', exitDate: null },
          { tenantName: 'Kiran Menon', roomType: 'single', roomNumber: '105', bedNumber: '01', joiningDate: '03 Feb 2026', exitDate: null },
          { tenantName: 'Deepak Joshi', roomType: 'quad', roomNumber: '203', bedNumber: '02', joiningDate: '07 Feb 2026', exitDate: '20 Feb 2026' },
        ],
        'March': [
          { tenantName: 'Ananya Singh', roomType: 'single', roomNumber: '106', bedNumber: '01', joiningDate: '02 Mar 2026', exitDate: null },
          { tenantName: 'Suresh Iyer', roomType: 'double', roomNumber: '107', bedNumber: '01', joiningDate: '05 Mar 2026', exitDate: null },
        ],
        'April': [],
        'May': [
          { tenantName: 'Rahul Sharma', roomType: 'single', roomNumber: '101', bedNumber: '01', joiningDate: '01 May 2026', exitDate: null },
          { tenantName: 'Pooja Gupta', roomType: 'dorm', roomNumber: '201', bedNumber: '02', joiningDate: '03 May 2026', exitDate: null },
          { tenantName: 'Naveen Pillai', roomType: 'double', roomNumber: '108', bedNumber: '02', joiningDate: '07 May 2026', exitDate: '15 May 2026' },
          { tenantName: 'Sonia Agarwal', roomType: 'single', roomNumber: '109', bedNumber: '01', joiningDate: '10 May 2026', exitDate: null },
        ],
        'June': [
          { tenantName: 'Ravi Kumar', roomType: 'triple', roomNumber: '204', bedNumber: '02', joiningDate: '01 Jun 2026', exitDate: null },
          { tenantName: 'Amit Verma', roomType: 'single', roomNumber: '110', bedNumber: '01', joiningDate: '04 Jun 2026', exitDate: null },
        ],
      },
      2025: {
        'January': [
          { tenantName: 'Rahul Sharma', roomType: 'single', roomNumber: '101', bedNumber: '01', joiningDate: '05 Jan 2025', exitDate: '20 Jan 2025' },
          { tenantName: 'Sneha Patel', roomType: 'double', roomNumber: '102', bedNumber: '01', joiningDate: '08 Jan 2025', exitDate: null },
        ],
        'February': [
          { tenantName: 'Arjun Rao', roomType: 'dorm', roomNumber: '201', bedNumber: '01', joiningDate: '02 Feb 2025', exitDate: null },
        ],
      },
      2024: {
        'January': [
          { tenantName: 'Priya Nair', roomType: 'single', roomNumber: '103', bedNumber: '01', joiningDate: '10 Jan 2024', exitDate: '25 Jan 2024' },
        ],
      }
    }
    return mockData
  }, [])

  if (loading) return <div className="rpt-state">Loading occupancy data…</div>
  if (error)   return <div className="rpt-state rpt-state-error">Error: {error}</div>

  return (
    <div className="rpt-section">

      <div className="rpt-tiles">
        <StatTile label="Total Beds"    value={bedStats.total}    accent="#6366F1" />
        <StatTile label="Occupied"      value={bedStats.occupied} sub={`${bedStats.rate}% fill rate`} accent="#22C55E" />
        <StatTile label="Notice Period" value={bedStats.notice}   sub="Moving out soon" accent="#F59E0B" />
        <StatTile label="Vacant"        value={bedStats.vacant}   sub="Available now"   accent="#EF4444" />
      </div>

      <div className="rpt-chart-grid">
        <div className="rpt-chart-card">
          <div className="rpt-chart-title">
            <span>Monthly Occupancy • {selectedYear}</span>
            <span className="sub">Target <strong>{target}%</strong></span>
          </div>
          <div className="rpt-legend">
            <span><span className="rpt-legend-dot met"></span> Met target</span>
            <span><span className="rpt-legend-dot missed"></span> Below target</span>
            <span><span className="rpt-legend-dot future"></span> No data yet</span>
          </div>
          <div className="rpt-barchart">
            {yearData.map((val, i) => {
              const isFuture = val === null || val === undefined
              const isPeak   = !isFuture && val >= peakVal && val === peakVal
              return (
                <div key={i} className="rpt-bar-col">
                  <span className="rpt-bar-value">{isFuture ? '—' : Math.round(val) + '%'}</span>
                  <div
                    className={`rpt-bar ${getBarClass(val)}`}
                    style={{ height: isFuture ? '4px' : `${Math.max(4, (val / 100) * 100)}%` }}
                  >
                    {isPeak && <span className="rpt-bar-peak-marker">▲</span>}
                  </div>
                  <span className="rpt-bar-label">{MONTHS[i]}</span>
                </div>
              )
            })}
          </div>
          <div className="rpt-target-section">
            <div className="rpt-target-label">
              Occupancy Target
              <input
                type="number"
                className="rpt-target-input"
                value={target}
                onChange={e => setTarget(Math.min(100, Math.max(50, Number(e.target.value) || 85)))}
                min={50} max={100}
              />
            </div>
            <div className="rpt-target-meta">
              <span>Above: <strong className="above">{validData.filter(v => v >= target).length}/{validData.length}</strong></span>
              <span>Below: <strong className="below">{validData.filter(v => v < target).length}/{validData.length}</strong></span>
            </div>
          </div>
        </div>

        <div className="rpt-quarter-card">
          <div className="rpt-quarter-label">Best Quarter</div>
          <div className="rpt-quarter-value">
            {(() => {
              let bestQ = { label: '', avg: 0, range: '' }
              for (let q = 0; q < 4; q++) {
                const start = q * 3
                const slice = yearData.slice(start, start + 3).filter(v => v !== null && v !== undefined)
                if (slice.length === 0) continue
                const avg = slice.reduce((a, b) => a + b, 0) / slice.length
                if (avg > bestQ.avg) {
                  bestQ = { label: `Q${q + 1}`, avg: Math.round(avg * 10) / 10, range: `${MONTHS[start]}–${MONTHS[start + 2]}` }
                }
              }
              return bestQ.label || '—'
            })()}
          </div>
          <div className="rpt-quarter-sub">
            {(() => {
              let bestQ = { label: '', avg: 0, range: '' }
              for (let q = 0; q < 4; q++) {
                const start = q * 3
                const slice = yearData.slice(start, start + 3).filter(v => v !== null && v !== undefined)
                if (slice.length === 0) continue
                const avg = slice.reduce((a, b) => a + b, 0) / slice.length
                if (avg > bestQ.avg) {
                  bestQ = { label: `Q${q + 1}`, avg: Math.round(avg * 10) / 10, range: `${MONTHS[start]}–${MONTHS[start + 2]}` }
                }
              }
              return bestQ.range ? `${bestQ.range} · ${bestQ.avg}% avg` : 'No data'
            })()}
          </div>
        </div>
      </div>

      <div className="rpt-health-card">
        <div className="rpt-health-summary">
          Your PG is running at <span className="highlight">{currentAvg.toFixed(1)}%</span> average occupancy this year —
          <span className={`rpt-health-tag ${healthTag}`}>{health.text}</span>.
          {validData.length < 12 && ` (based on ${validData.length} months of data)`}
        </div>
        <div className="rpt-health-meta">
          <span className="rpt-health-meta-item">Peak month: <strong>{peakMonth} – {peakVal}%</strong></span>
          <span className="rpt-health-meta-item">Lowest month: <strong>{lowestMonth} – {lowestVal}%</strong></span>
          <span className="rpt-health-meta-item">Year average: <strong>{currentAvg.toFixed(1)}%</strong></span>
          <span className="rpt-health-meta-item">
            vs {selectedYear - 1}: <strong className={vsPrev >= 0 ? 'up' : 'down'}>
              {vsPrev >= 0 ? `+${vsPrev.toFixed(1)}%` : `${vsPrev.toFixed(1)}%`}
            </strong>
          </span>
        </div>
      </div>

      {/* ── Month Wise Occupancy Accordion ── */}
      <OccupancyMonthAccordion 
        occupancyData={occupancyData} 
        selectedYear={selectedYear}
      />

    </div>
  )
}

export default OwnerOccupancyReport